import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { format } from 'date-fns';

import BaseDialog from 'core/components/base-dialog';
import { Button } from 'core/components/button';
import Divider from 'core/components/divider';
import { Label } from 'core/components/label';
import { useStore } from 'core/store';
import useSession from 'core/hooks/use-session';
import { track } from 'core/helpers/analytics';
import { awsDateToDateObject, numberFormatter } from 'core/helpers/formatters';
import { InlineCallout } from 'quote-revisit/components/inline-callout';
import { isPaymentScheduleOutOfDate } from 'quote-revisit/helpers/payment-schedule-out-of-date';
import {
  amountOf,
  getPriceDetails,
  PriceDetailsVariant
} from 'quote-revisit/modify-quote/left-column/policy-cards/price-details/get-price-details';
import { BillingDayMode, getBillingDay, LAST_BILLING_DAY, ordinalDay } from './billing-day';
import {
  getPaymentScheduleViewedProperties,
  PAYMENT_SCHEDULE_VIEWED,
  PaymentScheduleSource
} from './payment-schedule-viewed';
import rejectionIcon from './ic-rejection-16px.svg';
import useStyles from './payment-schedule-modal.styles';

const formatAmount = (amount) => `$${numberFormatter(amount, 2)}`;
const payments = (count) => `${count} ${count === 1 ? 'payment' : 'payments'}`;

// "6 payments of $98.83", or "$197.66 due at bind, then 4 payments of $98.83" when the 1st differs.
const getSummary = (policy) => {
  const priceDetails = getPriceDetails(policy);
  if (priceDetails?.variant === PriceDetailsVariant.EvenPayments) {
    return `${payments(priceDetails.paymentCount)} of ${formatAmount(priceDetails.paymentAmount)}`;
  }
  if (priceDetails?.variant === PriceDetailsVariant.DueAtBind) {
    return `${formatAmount(priceDetails.dueAtBindAmount)} due at bind, then ${payments(
      priceDetails.paymentCount
    )} of ${formatAmount(priceDetails.paymentAmount)}`;
  }
  return null;
};

const BillingDayCallout = ({ billingDay }) =>
  billingDay.mode === BillingDayMode.Custom ? (
    <InlineCallout variant="light">{`A custom billing day has been set: the ${ordinalDay(billingDay.day)}.`}</InlineCallout>
  ) : (
    <InlineCallout variant="light">
      {`The billing day follows the policy’s effective date: the ${ordinalDay(billingDay.day)}.`}
    </InlineCallout>
  );

BillingDayCallout.propTypes = {
  billingDay: PropTypes.shape({ day: PropTypes.number.isRequired, mode: PropTypes.string.isRequired }).isRequired
};

const ScheduleTable = ({ schedule }) => {
  const classes = useStyles();
  return (
    <table className={classes.table}>
      <thead>
        <tr>
          <th scope="col">Date due</th>
          <th scope="col">Payment number</th>
          <th scope="col" className={classes.amount}>
            Amount
          </th>
        </tr>
      </thead>
      <tbody>
        {schedule.map((payment, index) => (
          <tr key={payment.date}>
            <td>
              {format(awsDateToDateObject(payment.date), 'MMM d, yyyy')}
              {index === 0 && <span className={classes.dueAtBind}>(Due at bind)</span>}
            </td>
            <td>{`Payment ${index + 1} of ${schedule.length}`}</td>
            <td className={classes.amount}>{formatAmount(amountOf(payment))}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

ScheduleTable.propTypes = {
  schedule: PropTypes.arrayOf(PropTypes.shape({ date: PropTypes.string.isRequired })).isRequired
};

/**
 * A policy's payment schedule (#36360), read from the offer's saved schedule for that policy: every
 * payment's date, number and amount, and the term total. Opens from the Modify Quote policy card, and
 * read-only on Checkout (#36363).
 *
 * On Modify Quote, a schedule rated before today is out of date (see isPaymentScheduleOutOfDate), so it
 * asks for a quote update instead of listing stale dates. Checkout never shows that: an offer can only
 * bind on the day it was rated. Custom billing day controls (#36361) go above the payment dates, in
 * place of the billing day callout, when not read-only.
 */
const PaymentScheduleModal = ({ open, onClose, policy, readOnly }) => {
  const classes = useStyles();
  const { offer: store } = useStore();
  const session = useSession();
  const source = readOnly ? PaymentScheduleSource.Checkout : PaymentScheduleSource.ModifyQuote;

  // Once per open, not on every re-render of the page behind it.
  useEffect(() => {
    if (open) {
      track(
        PAYMENT_SCHEDULE_VIEWED,
        getPaymentScheduleViewedProperties({
          policy,
          offerId: store.offer?.id,
          selectedOption: store.selectedOption,
          state: store.state,
          source,
          session
        })
      );
    }
  }, [open]);

  const schedule = policy.schedule ?? [];
  const billingDay = getBillingDay(policy);
  const isOutOfDate = !readOnly && isPaymentScheduleOutOfDate(schedule);
  const summary = getSummary(policy);

  return (
    <BaseDialog open={open} onClose={onClose} size="md" fullWidth className={classes.dialog}>
      <div className={classes.content}>
        <Label type="h1" component="h2" className={classes.title}>
          Payment schedule
        </Label>
        {billingDay && <BillingDayCallout billingDay={billingDay} />}
        <section className={classes.section} aria-labelledby="payment-schedule-dates">
          <div className={classes.sectionHeading}>
            <Label type="body1Bold" component="h3" id="payment-schedule-dates" className={classes.heading}>
              Payment dates
            </Label>
            {!isOutOfDate && summary && (
              <Label type="body2" component="p" className={classes.summary}>
                {summary}
              </Label>
            )}
          </div>
          {isOutOfDate && (
            <InlineCallout variant="light" icon={rejectionIcon}>
              Update the quote to see payment dates and amounts.
            </InlineCallout>
          )}
          {billingDay?.mode === BillingDayMode.Capped28 && (
            <InlineCallout variant="light">
              {`This policy’s effective date is on the ${ordinalDay(billingDay.effectiveDay)}. Future payments will be charged on the ${ordinalDay(LAST_BILLING_DAY)}, since not every month has a ${ordinalDay(billingDay.effectiveDay)}.`}
            </InlineCallout>
          )}
          {!isOutOfDate && (
            <>
              <ScheduleTable schedule={schedule} />
              <div className={classes.termTotal}>
                <Divider className={classes.divider} />
                <div className={classes.termTotalRow}>
                  <Label type="body3" component="span" className={classes.termTotalLabel}>
                    Total term price
                  </Label>
                  <Label type="body2" component="span" className={classes.termTotalValue}>
                    {formatAmount(policy.total)}
                  </Label>
                </div>
              </div>
            </>
          )}
        </section>
        <div className={classes.actions}>
          <Button variant="contained" color="primary" onClick={onClose}>
            Done
          </Button>
        </div>
      </div>
    </BaseDialog>
  );
};

PaymentScheduleModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  policy: PropTypes.shape({
    key: PropTypes.string.isRequired,
    total: PropTypes.number,
    schedule: PropTypes.array,
    effectiveDate: PropTypes.string,
    billingDayOfMonth: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
  }).isRequired,
  readOnly: PropTypes.bool
};

PaymentScheduleModal.defaultProps = {
  readOnly: false
};

export default PaymentScheduleModal;
